// src/eden/context/EdenContextBridge.js
// EdenContextBridge (v1)
// - Central, modality-agnostic context builder for Eden (text, voice, realtime, etc.)
// - Page-aware + advertiser-aware + role-aware + channel inference
// - No UI code here; just context composition + subscriptions.
// - Safe defaults; NEVER persist tokens.

const DEFAULT_KEYS = {
  advertiserId: "selectedAdvertiserId",
  advertiserName: "selectedAdvertiserName",
  advertiserVertical: "selectedAdvertiserVertical",

  // Optional shared date range keys (only if you use them)
  dateStart: "selectedDateStart",
  dateEnd: "selectedDateEnd",

  // Optional role key if you set one (client/superadmin)
  role: "role",

  // Identity keys (superadmin commonly uses `email`, client uses `userEmail`)
  userId: "userId",
  email: "email",

  // Legacy / fallback keys we will READ (not write unless asked)
  legacyEmail: "userEmail",
  legacyAdvertiserName: "selectedAdvertiser",
};

function getLS(key) {
  try {
    const v = window?.localStorage?.getItem(key);
    return v ?? null;
  } catch {
    return null;
  }
}

function setLS(key, value) {
  try {
    window?.localStorage?.setItem(key, value);
  } catch {
    // ignore
  }
}

function normalizeId(val) {
  if (val === null || val === undefined) return null;
  if (typeof val === "number") return Number.isFinite(val) ? val : null;
  if (typeof val === "string") {
    const trimmed = val.trim();
    if (!trimmed) return null;

    const asNum = Number(trimmed);
    if (!Number.isNaN(asNum) && Number.isFinite(asNum)) return asNum;

    // allow non-numeric ids if you ever switch to UUID
    return trimmed;
  }
  return null;
}

function normalizeVertical(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  if (normalized === "auto") return "automotive";
  if (
    [
      "retail_e_commerce",
      "retail_ecommerce",
      "e_commerce",
      "ecommerce",
      "commerce",
      "ecomm",
    ].includes(normalized)
  ) {
    return "retail";
  }
  return ["general", "film", "automotive", "retail"].includes(normalized)
    ? normalized
    : "general";
}

function nowIso() {
  return new Date().toISOString();
}

// Simple, deterministic session id for continuity across multi-turn dialogue
function getOrCreateSessionId() {
  const KEY = "edenSessionId";
  const existing = getLS(KEY);
  if (existing) return existing;

  // lightweight unique-enough id
  const sid = `eden_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  setLS(KEY, sid);
  return sid;
}

function titleCaseToken(token) {
  const raw = String(token || "").trim();
  if (!raw) return "";

  const upperMap = {
    ctv: "CTV",
    dooh: "DOOH",
    ai: "AI",
    ga4: "GA4",
    api: "API",
    x: "X",
    id: "ID",
    cpa: "CPA",
    cpc: "CPC",
    ctr: "CTR",
    cpm: "CPM",
    kpi: "KPI",
    roas: "ROAS",
  };

  const lower = raw.toLowerCase();
  if (upperMap[lower]) return upperMap[lower];
  return lower.replace(/\b\w/g, (char) => char.toUpperCase());
}

function humanizeIdentifier(value) {
  const text = String(value || "").trim();
  if (!text) return "";

  return text
    .replace(/\/+$/, "")
    .split("/")
    .filter(Boolean)
    .pop()
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map(titleCaseToken)
    .join(" ")
    .trim();
}

function guessPageFromPath(pathname) {
  if (!pathname) return "Unknown";
  const cleaned = pathname.replace(/\/+$/, "") || "/";
  if (cleaned === "/") return "Dashboard";

  const last = cleaned.split("/").filter(Boolean).pop();
  if (!last) return "Dashboard";
  return humanizeIdentifier(last) || "Unknown";
}

function inferChannelFromPath(pathname, page) {
  const hay = `${String(pathname || "").toLowerCase()} ${String(page || "").toLowerCase()}`;

  // Match your ai_kpi_snapshots.channel values
  if (hay.includes("facebook")) return "facebook";
  if (hay.includes("instagram")) return "instagram";
  if (hay.includes("tiktok")) return "tiktok";
  if (hay.includes("youtube")) return "youtube";
  if (hay.includes("/x-") || hay.includes("x-analytics") || hay.includes(" xanalytics")) return "x";
  if (hay.includes("truthsocial")) return "truth_social";
  if (hay.includes("rumble")) return "rumble";
  if (hay.includes("google") && (hay.includes("search") || hay.includes("paid"))) return "google_search";
  if (hay.includes("google-analytics")) return "google_search";

  if (hay.includes("audio")) return "audio";
  if (hay.includes("ingame") || hay.includes("in-game")) return "ingame";
  if (hay.includes("ctv")) return "ctv";
  if (hay.includes("video-analytics")) return "video";
  if (hay.includes("mobile")) return "mobile";
  if (hay.includes("display")) return "display";
  if (hay.includes("dooh")) return "dooh";
  if (hay.includes("cinema")) return "cinema";

  // Rollups / hubs
  if (hay.includes("social-analytics")) return "social_rollup";
  if (hay.includes("platform-analytics")) return "platform_rollup";
  if (hay.includes("programmatic-analytics")) return "programmatic_rollup";

  // Unknown => Eden decides (all channels)
  return null;
}

function buildBaseContext({ pathname, overrides, keys }) {
  const roleRaw =
    overrides?.role ??
    getLS(keys.role) ??
    ""; // may be empty

  const role = String(roleRaw || "").toLowerCase() || null;

  // Token storage differs across apps sometimes (authToken, accessToken, token)
  // NOTE: We never persist token here.
  const token =
    overrides?.token ??
    getLS("token") ??
    getLS("authToken") ??
    getLS("accessToken") ??
    null;

  const advertiserId =
    overrides?.advertiserId ??
    normalizeId(getLS(keys.advertiserId));

  // prefer canonical key, but support legacy
  const advertiserName =
    overrides?.advertiserName ??
    getLS(keys.advertiserName) ??
    getLS(keys.legacyAdvertiserName) ??
    null;
  const advertiserVertical = normalizeVertical(
    overrides?.advertiserVertical ?? getLS(keys.advertiserVertical)
  );

  // Date range is optional
  const dateStart =
    overrides?.dateStart ??
    getLS(keys.dateStart);

  const dateEnd =
    overrides?.dateEnd ??
    getLS(keys.dateEnd);

  const userId =
    overrides?.userId ??
    normalizeId(getLS(keys.userId));

  const email =
    overrides?.email ??
    getLS(keys.email) ??
    getLS(keys.legacyEmail) ??
    null;

  const page = overrides?.page ?? guessPageFromPath(pathname);
  const channel = overrides?.channel ?? inferChannelFromPath(pathname, page);
  const pageTitle = overrides?.pageTitle ?? (humanizeIdentifier(overrides?.page ?? page) || page);
  const routeKey = overrides?.routeKey ?? pathname ?? null;
  const platformLabel =
    overrides?.platformLabel ??
    (channel ? humanizeIdentifier(String(channel).replace(/_rollup$/i, "")) : null);
  const toplineScope = overrides?.toplineScope ?? null;
  const pageToplineMetrics = Array.isArray(overrides?.pageToplineMetrics)
    ? overrides.pageToplineMetrics
    : [];
  const aggregateToplineMetrics = Array.isArray(overrides?.aggregateToplineMetrics)
    ? overrides.aggregateToplineMetrics
    : [];
  const pageCapabilities = Array.isArray(overrides?.pageCapabilities)
    ? overrides.pageCapabilities
    : [];
  const timeframe = overrides?.timeframe ?? null;
  const metadata = overrides?.metadata && typeof overrides.metadata === "object"
    ? overrides.metadata
    : {};
  const dashboardContext =
    overrides?.dashboardContext && typeof overrides.dashboardContext === "object"
      ? overrides.dashboardContext
      : null;

  const sessionId = overrides?.sessionId ?? getOrCreateSessionId();

  return {
    ts: nowIso(),
    sessionId,

    // Page awareness
    page,
    pageTitle,
    routeKey,
    route: pathname ?? null,
    channel, // null => all channels
    platformLabel,
    toplineScope,
    pageToplineMetrics,
    aggregateToplineMetrics,
    pageCapabilities,
    timeframe,
    metadata,
    ...(dashboardContext ? { dashboardContext } : {}),

    // identity / access
    role,
    user: {
      id: userId,
      email,
    },

    // tenant / scope
    advertiser: {
      id: advertiserId,
      name: advertiserName,
      vertical: advertiserVertical,
    },

    // optional time scope
    dateRange:
      dateStart && dateEnd
        ? { start: String(dateStart).slice(0, 10), end: String(dateEnd).slice(0, 10) }
        : null,

    // auth (do not log)
    auth: {
      hasToken: Boolean(token),
      token: token || null,
    },
  };
}

class EdenContextBridge {
  constructor(options = {}) {
    this.keys = { ...DEFAULT_KEYS, ...(options.keys || {}) };
    this.pathname =
      options.pathname ||
      (typeof window !== "undefined" ? window.location.pathname : "/");

    this.overrides = options.overrides || {};
    this.listeners = new Set();

    // External state can be injected from React contexts (AdvertiserContext/AuthContext)
    this.external = {
      advertiserId: null,
      advertiserName: null,
      advertiserVertical: "general",
      role: null,
      dateStart: null,
      dateEnd: null,
      userId: null,
      email: null,
      token: null,
      page: null,     // optional explicit page name override
      channel: null,  // optional explicit channel override
      dashboardContext: null,
      sessionId: null,
      ...(options.external || {}),
    };
  }

  // Call this from App.js on every route change
  setPathname(pathname) {
    if (!pathname || pathname === this.pathname) return;
    this.pathname = pathname;
    this._emit();
  }

  // Optional: set external state from React contexts
  setExternal(partial) {
    if (!partial || typeof partial !== "object") return;
    this.external = { ...this.external, ...partial };
    this._emit();
  }

  // Optional: set temporary overrides (e.g., current modal context)
  setOverrides(partial) {
    if (!partial || typeof partial !== "object") return;
    this.overrides = { ...this.overrides, ...partial };
    this._emit();
  }

  clearOverrides() {
    this.overrides = {};
    this._emit();
  }

  // Canonical context object Eden should receive
  getContext() {
    // External wins over localStorage; overrides win over both
    const mergedOverrides = {
      ...this.external,
      ...this.overrides,
    };

    // Persist ONLY safe, non-secret selections so Eden stays stable on reload.
    // Never write auth tokens here.
    if (mergedOverrides.advertiserId != null) {
      setLS(this.keys.advertiserId, String(mergedOverrides.advertiserId));
    }
    if (mergedOverrides.advertiserName) {
      setLS(this.keys.advertiserName, String(mergedOverrides.advertiserName));
    }
    if (mergedOverrides.advertiserVertical) {
      setLS(this.keys.advertiserVertical, normalizeVertical(mergedOverrides.advertiserVertical));
    }
    if (mergedOverrides.dateStart) {
      setLS(this.keys.dateStart, String(mergedOverrides.dateStart));
    }
    if (mergedOverrides.dateEnd) {
      setLS(this.keys.dateEnd, String(mergedOverrides.dateEnd));
    }
    if (mergedOverrides.role) {
      setLS(this.keys.role, String(mergedOverrides.role));
    }
    if (mergedOverrides.email) {
      // Don’t overwrite the client's `userEmail` legacy key; just store canonical `email`
      setLS(this.keys.email, String(mergedOverrides.email));
    }

    return buildBaseContext({
      pathname: this.pathname,
      overrides: mergedOverrides,
      keys: this.keys,
    });
  }

  // Subscribe to context updates (route changes, external updates, overrides)
  subscribe(listener) {
    if (typeof listener !== "function") return () => {};
    this.listeners.add(listener);

    // Immediately send current context
    try {
      listener(this.getContext());
    } catch {
      // ignore listener errors
    }

    return () => {
      this.listeners.delete(listener);
    };
  }

  _emit() {
    const ctx = this.getContext();
    this.listeners.forEach((fn) => {
      try {
        fn(ctx);
      } catch {
        // ignore listener errors
      }
    });
  }
}

// Export a singleton instance (simple + consistent across the app)
const edenContextBridge = new EdenContextBridge();

export { EdenContextBridge, edenContextBridge };
export default edenContextBridge;
